"use client";

import React, { useState, useEffect, useRef } from 'react';
import { useCart } from '@/app/contexts/cartContext';
import { MinusIcon, Trash2, ThumbsUp, ThumbsDown } from 'lucide-react';
import { useSession } from "next-auth/react";
import { v4 as uuidv4 } from 'uuid';

// --- Icons & UI Components ---

const ChatIcon = ({ className, color = 'white' }: { className?: string, color?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill={color} className={className}>
        <path fillRule="evenodd" d="M4.804 21.644A6.707 6.707 0 0 0 6 21.75a6.721 6.721 0 0 0 3.583-1.029c.774.182 1.584.279 2.417.279 5.322 0 9.75-3.97 9.75-9 0-5.03-4.428-9-9.75-9s-9.75 3.97-9.75 9c0 2.409 1.025 4.587 2.674 6.192.232.226.277.428.254.543a3.73 3.73 0 0 1-.814 1.686.75.75 0 0 0 .44 1.223ZM8.25 10.875a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25ZM10.875 12a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875-1.125a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Z" clipRule="evenodd" />
    </svg>
);

const SendIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
        <path fillRule="evenodd" d="M3 10a.75.75 0 0 1 .75-.75h10.638L10.23 5.29a.75.75 0 1 1 1.04-1.08l5.5 5.25a.75.75 0 0 1 0 1.08l-5.5 5.25a.75.75 0 1 1-1.04-1.08l4.158-3.96H3.75A.75.75 0 0 1 3 10Z" clipRule="evenodd" />
    </svg>
);

const LoadingSpinner = () => (
    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
);

const TypingDots = () => (
    <div className="flex space-x-1 p-1">
        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></div>
    </div>
);

type Message = {
    role: 'user' | 'bot' | 'system';
    text: string;
    chatId?: string;
    feedback?: string | null;
};

// --- Predefined Responses ---
const RESPONSES = {
    first: "We offer 5 bundles tailored to different business sizes: Foundation (Startup), Essential (Small), Growth (Medium), Premium (Large), and Enterprise. Each includes a specific set of services and tools to help your business grow.",
    second: "You can reach our customer support team by emailing hello@sdemarketplace.my or using the live chat feature within the portal. Our team aims to respond to all inquiries within one business day.",
    third: "No, there are no hidden fees. The price displayed for your selected bundle is the amount you will be charged annually, plus any applicable taxes (e.g., SST).",
    default: "Thank you for your question. Our support team is currently reviewing your request and will provide more details shortly."
};

// --- Helper to Generate formatted Session ID ---
const generateSessionId = () => `session_${uuidv4()}`;

const ChatbotButton = () => {
    // Get the session data
    const { data: session } = useSession();
    const currentUserId = session?.user?.id ? parseInt(session.user.id) : null;

    // WhatsApp Link Logic 
    const phoneNumber = process.env.NEXT_PUBLIC_WHATSAPP_NUMBER;
    let waMessage = "Hi, I am interested in SDeMARKETPLACE.";

    if (session?.user) {
        const userName = session.user.name || "Registered User";
        waMessage = `Hi, I'm ${userName}. I am interested in SDeMARKETPLACE.`;
    }
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(waMessage)}`;

    // Cart Context
    const { isCartOpen } = useCart();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMounted, setIsMounted] = useState(false);

    // UI State
    const [isOpen, setIsOpen] = useState(false);
    const [hasStarted, setHasStarted] = useState(false); // Switch between Welcome and Chat view
    const [isLoading, setIsLoading] = useState(false);

    // Data State
    const [sessionId, setSessionId] = useState("");
    const [initialInput, setInitialInput] = useState(""); // Input for the "Welcome" screen
    const [chatInput, setChatInput] = useState(""); // Input for the "Chat" screen
    const [messages, setMessages] = useState<Message[]>([]);

    // Modal State
    const [isClearModalOpen, setIsClearModalOpen] = useState(false);
    const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Refs
    const chatEndRef = useRef<HTMLDivElement>(null);
    const initialTextareaRef = useRef<HTMLTextAreaElement>(null);
    const chatTextareaRef = useRef<HTMLTextAreaElement>(null);
    const apiUrl = '/api/chatbot';

    // --- Idle Timer Logic ---
    const startIdleTimer = () => {
        // Clear existing timer
        if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);

        // Set new timer for 10 minutes (600,000 ms)
        idleTimeoutRef.current = setTimeout(() => {
            setMessages(prev => [
                ...prev,
                { role: 'system', text: "You've been idle for a while." }
            ]);
        }, 600000); // 10 minutes
    };

    // Effect to manage timer start/stop based on chat status
    useEffect(() => {
        // Check the last message 
        const lastMessage = messages[messages.length - 1];

        // Do not start timer if chat hasn't started, is loading, or last message is system
        if (!hasStarted || isLoading || lastMessage?.role === 'system') {
            if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
            return;
        }

        // Otherwise, start the timer
        startIdleTimer();

        return () => {
            if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
        };
    }, [hasStarted, isLoading, messages]); // Reset when messages change

    // --- Effects ---
    useEffect(() => {
        setIsMounted(true);

        const syncGuestSession = async () => {
            // 1. Check if there's an old session ID stored in the browser's memory
            const oldGuestSession = localStorage.getItem("last_guest_session");

            // 2. If it exists AND the user is NOT logged in, tell backend to hide it
            if (oldGuestSession && !currentUserId) {
                try {
                    await fetch(`${apiUrl}/clear`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            userId: null,
                            sessionId: oldGuestSession
                        }),
                    });
                } catch (err) {
                    console.error("Failed to expire old guest session", err);
                }
            }

            // 3. Generate a BRAND NEW session ID for this refresh
            const newSession = `session_${uuidv4()}`;
            setSessionId(newSession);

            // 4. Store it so we can clean it up on the NEXT refresh
            localStorage.setItem("last_guest_session", newSession);
        };

        syncGuestSession();

        const handleScroll = () => {
            setIsScrolled(window.scrollY > 100);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [currentUserId, apiUrl]);

    // Fetch History (Registered Users Only)
    useEffect(() => {
        const fetchHistory = async () => {
            // Only fetch if we have a registered user and the chat is open
            if (currentUserId && isOpen) {
                try {
                    // Clear messages first to ensure we don't see previous session's data
                    setMessages([]);

                    const res = await fetch(`${apiUrl}/history?userId=${currentUserId}`);
                    if (!res.ok) return;

                    const data = await res.json();

                    if (data.history && data.history.length > 0) {
                        setMessages(data.history);
                        setHasStarted(true);
                    } else {
                        // No history found for this user, show welcome screen
                        setHasStarted(false);
                    }
                } catch (err) {
                    console.error("Failed to load history", err);
                }
            }
        };

        fetchHistory();
    }, [isOpen, currentUserId, apiUrl]);

    // Scroll to bottom when messages change
    useEffect(() => {
        if (hasStarted && chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isLoading, hasStarted]);

    // Auto-resize Welcome textarea
    useEffect(() => {
        if (initialTextareaRef.current) {
            initialTextareaRef.current.style.height = "auto";
            const newHeight = Math.min(initialTextareaRef.current.scrollHeight, 130);
            initialTextareaRef.current.style.height = `${newHeight}px`;
        }
    }, [initialInput]);

    // Auto-resize Chat textarea
    useEffect(() => {
        if (chatTextareaRef.current) {
            chatTextareaRef.current.style.height = "auto";
            const newHeight = Math.min(chatTextareaRef.current.scrollHeight, 200);
            chatTextareaRef.current.style.height = `${newHeight}px`;
        }
    }, [chatInput]);

    useEffect(() => {
        if (isOpen && window.innerWidth < 768) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [isOpen]);

    // Reset Chatbot when User changes (Login/Logout) 
    useEffect(() => {
        const handleUserTransition = async () => {
            // 1. Clear Backend Data for the previous session (Important for Guest Users who just logged in)
            if (currentUserId && sessionId) {
                try {
                    await fetch(`${apiUrl}/clear`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            userId: null, // Specifically target the guest version
                            sessionId: sessionId
                        }),
                    });
                } catch (err) {
                    console.error("Failed to clear guest session on login", err);
                }
            }

            // 2. Reset Frontend State
            setMessages([]);
            setHasStarted(false);
            setIsLoading(false);

            // 3. Generate a fresh Session ID for the registered user
            const newSession = `session_${uuidv4()}`;
            setSessionId(newSession);

            // 4. Update the storage
            localStorage.setItem("last_guest_session", newSession);
        };

        handleUserTransition();
    }, [currentUserId]);

    // --- Handlers ---

    const handleFeedback = async (chatId: string | undefined, type: 'good' | 'bad') => {
        if (!chatId) return;

        // 1. Optimistically update UI so the user sees the color change instantly & buttons lock
        setMessages(prevMessages =>
            prevMessages.map(msg =>
                msg.chatId === chatId ? { ...msg, feedback: type } : msg
            )
        );

        // 2. Call the API to save to database
        try {
            await fetch('/api/chatbot/feedback', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ chatId, feedback: type }),
            });
        } catch (error) {
            console.error("Failed to submit feedback", error);
        }
    };

    const handleChatClick = () => setIsOpen(!isOpen);

    const handleClearClick = () => {
        setIsClearModalOpen(true);
    };

    const confirmReset = async () => {
        // 1. Call Backend to Clear
        try {
            await fetch(`${apiUrl}/clear`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: currentUserId, // If null, backend treats as guest
                    sessionId: sessionId
                }),
            });
        } catch (error) {
            console.error("Sync error", error);
        }

        // 2. Reset Frontend
        setHasStarted(false);
        setMessages([]);
        setInitialInput("");
        setChatInput("");
        setIsLoading(false);

        // 3. New Session ID (Essential for Guest Cache logic)
        setSessionId(generateSessionId());

        if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
        setIsClearModalOpen(false);
    };

    const processResponse = async (userText: string, specificKey?: string) => {
        setHasStarted(true);

        // 1. Add User Message to UI
        setMessages(prev => [...prev, { role: 'user', text: userText }]);
        setInitialInput("");
        setChatInput("");
        setIsLoading(true);

        try {
            // 2. Call the Next.js API
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: userText,
                    userId: currentUserId,
                    sessionId: sessionId
                }),
            });

            if (!response.ok) throw new Error("API Error");
            const data = await response.json();

            // 3. Display Bot Message
            setMessages(prev => [...prev, { role: 'bot', text: data.text, chatId: data.chatId, feedback: null }]);

        } catch (error) {
            console.error("Chat Error:", error);

            // Fallback: If the server is totally down, we can still use the local RESPONSES
            const localResponse = specificKey && RESPONSES[specificKey as keyof typeof RESPONSES]
                ? RESPONSES[specificKey as keyof typeof RESPONSES]
                : "I'm having trouble connecting right now. Please try again later.";

            setMessages(prev => [...prev, { role: 'bot', text: localResponse }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleInitialSubmit = () => {
        if (!initialInput.trim()) return;
        processResponse(initialInput);
    };

    const handleChatSubmit = () => {
        if (!chatInput.trim()) return;
        processResponse(chatInput);
    };

    const buttonPositionClass = isScrolled ? "bottom-8 md:bottom-20" : "bottom-8";

    if (!isMounted) return null;

    return (
        !isCartOpen && (
            <>
                {isOpen && (
                    <div className={`fixed inset-0 z-[60] flex flex-col w-full h-full bg-white transition-all duration-300 animate-in fade-in slide-in-from-bottom-4 md:inset-auto md:bottom-8 md:right-24 md:w-[360px] md:h-[550px] md:rounded-xl md:border md:border-[#1e2d4d]/30 md:shadow-2xl md:overflow-hidden`}>

                        {!hasStarted ? (
                            // --- WELCOME STATE ---
                            <>
                                {/* Welcome Header */}
                                <div className="bg-[#1e2d4d] pt-12 px-5 pb-4 text-white rounded-b-2xl">
                                    <div className="flex items-start justify-between mb-6">
                                        <div className="pr-10">
                                            <h3 className="text-xl font-bold leading-tight">Ask SDeMARKETPLACE</h3>
                                            <p className="text-xs text-blue-100 mt-1 opacity-90">Get helpful guidance and recommendations.</p>
                                        </div>
                                        <button
                                            onClick={() => setIsOpen(false)}
                                            className="rounded-full p-1 hover:bg-white/20 transition-colors -mt-9 -mr-1"
                                        >
                                            <MinusIcon className="w-5 h-5" />
                                        </button>
                                    </div>

                                    <div className="relative">
                                        <textarea
                                            ref={initialTextareaRef}
                                            rows={1}
                                            placeholder="Ask a question"
                                            value={initialInput}
                                            onChange={(e) => setInitialInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    handleInitialSubmit();
                                                }
                                            }}
                                            className="w-full rounded-md border-0 py-2.5 pl-4 pr-12 text-sm shadow-sm bg-white text-gray-800 placeholder:text-gray-500 focus:ring-2 focus:ring-indigo-400 outline-none overflow-y-auto resize-none"
                                        />

                                        <button
                                            onClick={handleInitialSubmit}
                                            disabled={!initialInput.trim()}
                                            className={`absolute right-1.5 bottom-3 rounded-full p-1.5 transition-all flex items-center justify-center
                                                ${initialInput.trim()
                                                    ? "bg-gray-100 text-[#1e2d4d] hover:bg-gray-200 hover:text-indigo-600 cursor-pointer active:scale-95"
                                                    : "bg-gray-50 text-gray-300 cursor-not-allowed opacity-50"
                                                }`}
                                            aria-label="Send message"
                                        >
                                            <SendIcon />
                                        </button>
                                    </div>
                                </div>

                                {/* Welcome Body */}
                                <div className="p-5 pt-8 pb-10 bg-white flex flex-col gap-5 flex-1 md:min-h-[360px] overflow-y-auto">
                                    <div>
                                        <h4 className="text-sm font-bold text-gray-800">Want help getting started?</h4>
                                        <p className="text-sm text-gray-600 mt-1">Tell us a little bit about what you're looking for.</p>
                                    </div>

                                    <div className="flex flex-col gap-3">
                                        <button
                                            onClick={() => processResponse("I want to learn more about Bundles", 'first')}
                                            className="text-left w-full rounded-lg border border-[#1e2d4d]/30 bg-white px-4 py-3 text-sm font-medium text-[#1e2d4d] transition-all hover:bg-[#1e2d4d]/5 hover:border-[#1e2d4d] shadow-sm"
                                        >
                                            I want to learn more about Bundles
                                        </button>
                                        <button
                                            onClick={() => processResponse("How can I contact customer support", 'second')}
                                            className="text-left w-full rounded-lg border border-[#1e2d4d]/30 bg-white px-4 py-3 text-sm font-medium text-[#1e2d4d] transition-all hover:bg-[#1e2d4d]/5 hover:border-[#1e2d4d] shadow-sm"
                                        >
                                            How can I contact customer support
                                        </button>
                                        <button
                                            onClick={() => processResponse("Are there any hidden fees", 'third')}
                                            className="text-left w-full rounded-lg border border-[#1e2d4d]/30 bg-white px-4 py-3 text-sm font-medium text-[#1e2d4d] transition-all hover:bg-[#1e2d4d]/5 hover:border-[#1e2d4d] shadow-sm"
                                        >
                                            Are there any hidden fees
                                        </button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            // --- ACTIVE CHAT STATE ---
                            <div className="flex flex-col flex-1 h-full">
                                {/* Chat Header */}
                                <div className="bg-[#1e2d4d] px-4 py-3 text-white shrink-0 z-20">
                                    <div className="flex items-center justify-between">
                                        <button
                                            onClick={handleClearClick}
                                            className="rounded-full p-1.5 hover:bg-white/20 transition-colors text-white/90 hover:text-red-300"
                                        >
                                            <Trash2 className="w-4.5 h-4.5" />
                                        </button>

                                        <h3 className="text-base font-bold">Ask SDeMARKETPLACE</h3>

                                        <button
                                            onClick={() => setIsOpen(false)}
                                            className="rounded-full p-1.5 hover:bg-white/20 transition-colors"
                                        >
                                            <MinusIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>

                                {/* Chat Body (Messages) */}
                                <div className="flex-1 overflow-y-auto p-4 space-y-6 bg-white">
                                    {messages.map((msg, idx) => {
                                        // Idle Message
                                        if (msg.role === 'system') {
                                            return (
                                                <div key={idx} className="flex items-center justify-center w-full my-6">
                                                    <div className="h-px bg-gray-300 flex-1 border-t border-dashed border-gray-300"></div>
                                                    <span className="px-4 text-xs text-gray-500 font-medium whitespace-nowrap">
                                                        {msg.text}
                                                    </span>
                                                    <div className="h-px bg-gray-300 flex-1 border-t border-dashed border-gray-300"></div>
                                                </div>
                                            );
                                        }

                                        // User or Bot Message
                                        return (
                                            <div key={idx} className={`flex w-full ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                                {msg.role === 'user' ? (
                                                    <div className="bg-gray-100 text-gray-800 px-4 py-3 rounded-lg rounded-tr-none max-w-[85%] text-sm shadow-sm border border-gray-100 font-medium">
                                                        {msg.text}
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col gap-1 max-w-[95%]">
                                                        {/* Bot Message Text */}
                                                        <div className="text-gray-800 text-sm leading-relaxed pl-2">
                                                            {msg.text.split(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+|WhatsApp us)/gi).map((part, i) => {
                                                                if (part.match(/[a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+/gi)) {
                                                                    return (
                                                                        <a key={i} href={`mailto:${part}`} className="text-indigo-600 underline hover:text-indigo-800">
                                                                            {part}
                                                                        </a>
                                                                    );
                                                                } else if (part.toLowerCase() === 'whatsapp us') {
                                                                    return (
                                                                        <a key={i} href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline hover:text-indigo-800">
                                                                            {part}
                                                                        </a>
                                                                    );
                                                                } else {
                                                                    return part;
                                                                }
                                                            })}
                                                        </div>

                                                        {/* Feedback Buttons */}
                                                        {msg.chatId && (
                                                            <div className="flex items-center gap-2 pl-1.5">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleFeedback(msg.chatId, 'good')}
                                                                    disabled={msg.feedback === 'good' || msg.feedback === 'bad'}
                                                                    className={`p-1.5 rounded-md transition-all ${msg.feedback === 'good'
                                                                        ? 'text-gray-800 cursor-default'
                                                                        : msg.feedback === 'bad'
                                                                            ? 'text-gray-300 cursor-not-allowed'
                                                                            : 'text-gray-400 hover:text-gray-800 hover:bg-gray-100 active:scale-95'
                                                                        }`}
                                                                >
                                                                    <ThumbsUp className="w-4 h-4" />
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleFeedback(msg.chatId, 'bad')}
                                                                    disabled={msg.feedback === 'good' || msg.feedback === 'bad'}
                                                                    className={`p-1.5 rounded-md transition-all ${msg.feedback === 'bad'
                                                                        ? 'text-gray-800 cursor-default'
                                                                        : msg.feedback === 'good'
                                                                            ? 'text-gray-300 cursor-not-allowed'
                                                                            : 'text-gray-400 hover:text-gray-800 hover:bg-gray-100 active:scale-95'
                                                                        }`}
                                                                >
                                                                    <ThumbsDown className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}

                                    {/* Bot Thinking Animation (Dots) */}
                                    {isLoading && (
                                        <div className="flex justify-start w-full">
                                            <div className="pl-1">
                                                <TypingDots />
                                            </div>
                                        </div>
                                    )}
                                    <div ref={chatEndRef} />
                                </div>

                                {/* Chat Footer (Input) */}
                                <div className="border-t border-gray-100 bg-white p-4">
                                    {isLoading ? (
                                        // Loading State - Spinner
                                        <div className="flex items-center justify-start px-4 w-full h-[46px] bg-white border rounded-xl border-gray-200 shadow-sm">
                                            <LoadingSpinner />
                                            <span className="text-sm font-medium text-gray-500 animate-pulse ml-1">Responding...</span>
                                        </div>
                                    ) : (
                                        // Active Input State
                                        <div className="relative flex items-end gap-2 bg-white rounded-xl border border-gray-300 shadow-sm px-3 py-2 focus-within:ring-2 focus-within:ring-indigo-400 focus-within:border-transparent transition-all">
                                            <textarea
                                                ref={chatTextareaRef}
                                                rows={1}
                                                placeholder="Ask a question"
                                                value={chatInput}
                                                onChange={(e) => setChatInput(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        handleChatSubmit();
                                                    }
                                                }}
                                                className="w-full bg-transparent text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none resize-none py-2 max-h-[150px] overflow-y-auto"
                                            />
                                            <button
                                                onClick={handleChatSubmit}
                                                disabled={!chatInput.trim()}
                                                className={`p-2 rounded-full mb-0.5 shrink-0 transition-colors flex items-center justify-center ${chatInput.trim()
                                                    ? 'bg-[#1e2d4d] text-white hover:bg-[#15203a] active:scale-95'
                                                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                    }`}
                                            >
                                                <SendIcon />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Clear Confirmation Modal */}
                        {isClearModalOpen && (
                            <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[1px] p-4 animate-in fade-in duration-200">
                                <div className="bg-white rounded-xl shadow-xl w-full max-w-[280px] overflow-hidden">
                                    {/* Modal Header */}
                                    <div className="flex items-center justify-between p-4 pb-2">
                                        <h3 className="text-lg font-bold text-gray-900">Clear conversation</h3>
                                        <button
                                            onClick={() => setIsClearModalOpen(false)}
                                            className="text-gray-500 hover:text-gray-700 transition-colors"
                                        >
                                        </button>
                                    </div>

                                    {/* Modal Body */}
                                    <div className="px-4 py-2">
                                        <p className="text-xs text-gray-600 leading-relaxed font-medium">
                                            All messages in the chat will be cleared. <br />
                                            Would you like to clear this conversation?
                                        </p>
                                    </div>

                                    {/* Modal Footer */}
                                    <div className="p-4 flex gap-2 justify-end mt-2 border-t border-gray-200">
                                        <button
                                            onClick={() => setIsClearModalOpen(false)}
                                            className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={confirmReset}
                                            className="px-4 py-2 text-sm font-semibold text-white bg-[#1e2d4d] rounded-lg hover:bg-[#15203a] transition-colors"
                                        >
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Floating Toggle Button */}
                <button
                    onClick={handleChatClick}
                    className={`fixed ${buttonPositionClass} right-8 z-50 items-center justify-center rounded-lg bg-[#1e2d4d] text-white shadow-lg transition-all duration-300 hover:scale-110 hover:bg-[#15203a] focus:outline-none h-12 w-12 md:h-10 md:w-10 
                    ${isOpen ? 'hidden md:flex' : 'flex'}`}
                >
                    <ChatIcon className="h-7 w-7 md:h-6 md:w-6" color="white" />
                </button>
            </>
        )
    );
};

export default ChatbotButton;